import React from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, Star } from "lucide-react";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import { PageHero, SectionHeading, Icon, CTABand } from "../components/ui-blocks";
import { pricing, company } from "../mock/mock";

function CellValue({ v }) {
  if (v === true) return <Check className="mx-auto h-5 w-5 text-brand-teal" />;
  if (v === false) return <X className="mx-auto h-5 w-5 text-slate-300" />;
  return <span className="text-sm text-slate-600">{v}</span>;
}

export default function Pricing() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "VerifiAssist Dedicated Virtual Staffing",
    description:
      "Flat-rate dedicated remote staffing for dental and orthodontic practices — insurance verification, billing, scheduling and follow-up.",
    brand: { "@type": "Organization", name: company.legal },
    offers: pricing.plans
      .filter((p) => p.price.startsWith("$"))
      .map((p) => ({
        "@type": "Offer",
        name: p.name,
        price: p.price.replace(/[$,]/g, ""),
        priceCurrency: "USD",
        description: p.summary,
      })),
  };

  return (
    <>
      <SEO
        title="Pricing — Dedicated Virtual Staffing for Dental & Orthodontic Practices"
        description="Simple flat-rate plans for dedicated remote insurance verification and admin support. Growth from $1,650/mo and Full Coverage from $3,000/mo — no long-term lock-in, no hiring or turnover risk."
        path="/pricing"
        jsonLd={jsonLd}
      />
      <PageHero eyebrow={pricing.hero.eyebrow} title={pricing.hero.title} subtitle={pricing.hero.subtitle}>
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <Button asChild size="lg" className="bg-brand-600 hover:bg-brand-700"><Link to="/contact-us">Book a Discovery Call <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
      </PageHero>

      {/* PLANS */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="Plans" title="Pick a plan sized to your volume" subtitle="Predictable monthly pricing with a dedicated team that works your standard business hours." />
        <div className="mt-12 grid gap-6 lg:grid-cols-3 items-stretch">
          {pricing.plans.map((p) => (
            <div
              key={p.name}
              className={`relative flex flex-col rounded-3xl border p-7 shadow-sm ${
                p.highlight ? "border-brand-600 bg-brand-navy text-white shadow-xl lg:-mt-4" : "border-slate-200 bg-white"
              }`}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-brand-teal px-3 py-1 text-xs font-semibold text-white">
                  <Star className="h-3 w-3 fill-current" /> Most popular
                </span>
              )}
              <h3 className={`text-lg font-semibold ${p.highlight ? "text-white" : "text-brand-navy"}`}>{p.name}</h3>
              <p className={`mt-1 text-sm ${p.highlight ? "text-brand-100/80" : "text-slate-500"}`}>{p.tagline}</p>
              <div className="mt-5 flex items-end gap-1">
                <span className={`text-4xl font-bold font-display ${p.highlight ? "text-white" : "text-brand-navy"}`}>{p.price}</span>
                {p.period && <span className={`mb-1 text-sm ${p.highlight ? "text-brand-100/70" : "text-slate-500"}`}>{p.period}</span>}
              </div>
              <p className={`mt-2 text-sm font-medium ${p.highlight ? "text-brand-teal" : "text-brand-600"}`}>{p.summary}</p>

              <ul className="mt-6 flex-1 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${p.highlight ? "bg-brand-teal/20 text-brand-teal" : "bg-brand-50 text-brand-600"}`}>
                      <Check className="h-3.5 w-3.5" />
                    </span>
                    <span className={`text-sm leading-relaxed ${p.highlight ? "text-brand-100/90" : "text-slate-600"}`}>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                asChild
                size="lg"
                className={`mt-7 w-full ${p.highlight ? "bg-white text-brand-700 hover:bg-brand-50" : "bg-brand-600 text-white hover:bg-brand-700"}`}
              >
                <Link to="/contact-us">{p.cta}</Link>
              </Button>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-slate-500">{pricing.note}</p>
      </section>

      {/* INCLUDED */}
      <section className="bg-slate-50">
        <div className="container-x py-16 sm:py-20">
          <SectionHeading center eyebrow="No fine print" title={pricing.includedTitle} />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {pricing.included.map((it) => (
              <div key={it.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600"><Icon name={it.icon} className="h-5 w-5" /></div>
                <h3 className="mt-4 text-base font-semibold text-brand-navy">{it.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{it.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COST OF INACTION */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="The ROI case" title={pricing.cost.title} subtitle={pricing.cost.subtitle} />
        <div className="mt-12 grid gap-6 sm:grid-cols-3">
          {pricing.cost.stats.map((s) => (
            <div key={s.value} className="rounded-2xl border border-slate-200 bg-white p-7 text-center shadow-sm">
              <div className="text-4xl font-bold font-display text-brand-600">{s.value}</div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">{pricing.cost.note}</p>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-slate-50">
        <div className="container-x py-16 sm:py-20">
          <SectionHeading center eyebrow="Compare" title={pricing.compare.title} />
          <div className="mt-10 overflow-x-auto">
            <div className="min-w-[640px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/60">
                    <th className="p-5 text-left text-sm font-semibold text-brand-navy">&nbsp;</th>
                    {pricing.compare.columns.map((c, i) => (
                      <th
                        key={c}
                        className={`p-5 text-center text-sm font-semibold ${i === 2 ? "bg-brand-50 text-brand-700" : "text-slate-500"}`}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pricing.compare.rows.map((row, ri) => (
                    <tr key={ri} className="border-b border-slate-100 last:border-0">
                      <td className="p-5 text-sm font-medium text-brand-navy">{row.label}</td>
                      {row.values.map((v, vi) => (
                        <td key={vi} className={`p-5 text-center ${vi === 2 ? "bg-brand-50/50" : ""}`}>
                          <CellValue v={v} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="container-x py-16 sm:py-20">
        <SectionHeading center eyebrow="Case studies" title={pricing.caseStudies.title} subtitle={pricing.caseStudies.subtitle} />
        <div className="mt-12 grid gap-6 lg:grid-cols-3 items-stretch">
          {pricing.caseStudies.items.map((cs) => (
            <div key={cs.practice} className="flex flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold text-brand-700">
                  <Icon name="Building2" className="h-3.5 w-3.5" /> {cs.plan}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-brand-navy">{cs.practice}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{cs.summary}</p>
              <div className="mt-6 space-y-3">
                {cs.metrics.map((m) => (
                  <div key={m.label} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                    <div className="text-xs font-medium text-slate-500">{m.label}</div>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm font-semibold text-slate-400 line-through decoration-slate-300">{m.before}</span>
                      <Icon name="ArrowRight" className="h-4 w-4 text-brand-400" />
                      <span className="text-lg font-bold font-display text-brand-navy">{m.after}</span>
                      <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-brand-teal/10 px-2.5 py-1 text-xs font-semibold text-brand-teal-dark">
                        <Icon name="TrendingUp" className="h-3.5 w-3.5" /> {m.improve}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-xs text-slate-400">{pricing.caseStudies.disclaimer}</p>
      </section>

      {/* STEPS */}
      <section className="bg-brand-navy">
        <div className="container-x py-16 sm:py-20">
          <SectionHeading center light eyebrow="Onboarding" title={pricing.steps.title} subtitle="A straightforward, guided onboarding — live in days, not months." />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {pricing.steps.items.map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="text-3xl font-bold font-display text-brand-teal">{s.n}</div>
                <h3 className="mt-3 text-lg font-semibold text-white">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-100/80">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-x py-16 sm:py-20">
        <div className="mx-auto max-w-3xl">
          <SectionHeading center eyebrow="Pricing FAQ" title="Questions about plans & pricing" />
          <Accordion type="single" collapsible className="mt-8">
            {pricing.faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="rounded-xl border border-slate-200 bg-white mb-3 px-5">
                <AccordionTrigger className="text-left text-brand-navy hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <CTABand
        title="Give your front desk its time back"
        body="Book a discovery call and we'll finalize the right plan and scope for your practice — no obligation."
        primaryLabel="Book a Discovery Call"
        secondaryTo="/dental-orthodontics"
        secondaryLabel="See dental & ortho support"
      />
    </>
  );
}
